import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../core/auth.service';
import { LayoutService } from '../../core/layout.service';
import { IconComponent, IconName } from '../icon/icon.component';

interface NavChild {
  path: string;
  labelKey: string;
}

interface NavItem {
  path: string;
  labelKey: string;
  icon: IconName;
  /** Omit for items every authenticated user can see; set to gate by role (any match shows the item). */
  roles?: string[];
  /**
   * Present = the item is an expandable group rather than a link, and `path` only names the section
   * it owns (used to decide when the group opens itself). Give the section's own landing page its
   * own child so every destination still has an explicit label.
   */
  children?: NavChild[];
}

const ALL_NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', labelKey: 'shared.nav.homeDashboard', icon: 'dashboard' },
  { path: '/work-orders', labelKey: 'shared.nav.workOrders', icon: 'clipboard-list' },
  { path: '/maintenance-requests', labelKey: 'shared.nav.maintenanceRequests', icon: 'wrench' },
  {
    path: '/inventory',
    labelKey: 'shared.nav.inventory',
    icon: 'boxes',
    roles: ['StoreInventoryManager'],
    children: [
      { path: '/inventory', labelKey: 'shared.nav.inventoryStock' },
      { path: '/inventory/usage-report', labelKey: 'shared.nav.inventoryUsageReport' },
      { path: '/inventory/transactions', labelKey: 'shared.nav.inventoryTransactions' },
      { path: '/inventory/adjustments', labelKey: 'shared.nav.inventoryAdjustments' }
    ]
  },
  { path: '/stores', labelKey: 'shared.nav.stores', icon: 'warehouse', roles: ['StoreInventoryManager', 'FacilityManager'] },
  { path: '/parts', labelKey: 'shared.nav.parts', icon: 'package', roles: ['StoreInventoryManager'] },
  {
    path: '/purchase-orders',
    labelKey: 'shared.nav.purchaseOrders',
    icon: 'cart',
    roles: ['StoreInventoryManager', 'FacilityManager']
  },
  {
    path: '/vendors',
    labelKey: 'shared.nav.vendors',
    icon: 'truck',
    roles: ['StoreInventoryManager', 'FacilityManager', 'SystemAdmin']
  },
  { path: '/assets', labelKey: 'shared.nav.assets', icon: 'server', roles: ['AssetManager'] },
  { path: '/admin/buildings', labelKey: 'shared.nav.buildings', icon: 'building', roles: ['SystemAdmin', 'FacilityManager'] },
  {
    path: '/admin/priority-matrix',
    labelKey: 'shared.nav.priorityMatrix',
    icon: 'sliders',
    roles: ['SystemAdmin', 'FacilityManager']
  },
  {
    path: '/admin/maintenance-categories',
    labelKey: 'shared.nav.maintenanceCategories',
    icon: 'tag',
    roles: ['SystemAdmin', 'FacilityManager']
  },
  { path: '/admin/users', labelKey: 'shared.nav.adminUsers', icon: 'users', roles: ['SystemAdmin'] },
  { path: '/admin/audit-log', labelKey: 'shared.nav.auditLog', icon: 'history', roles: ['SystemAdmin'] },
  { path: '/admin/imports', labelKey: 'shared.nav.imports', icon: 'upload', roles: ['SystemAdmin'] },
  {
    path: '/admin/notification-templates',
    labelKey: 'shared.nav.notificationTemplates',
    icon: 'mail',
    roles: ['SuperAdmin']
  },
  { path: '/admin/notification-log', labelKey: 'shared.nav.notificationLog', icon: 'bell', roles: ['SystemAdmin'] },
  { path: '/settings', labelKey: 'shared.nav.settings', icon: 'settings' },
  { path: '/admin/agents', labelKey: 'shared.nav.agents', icon: 'bot', roles: ['SystemAdmin', 'FacilityManager'] },
  { path: '/admin/approval-routing', labelKey: 'shared.nav.approvalRouting', icon: 'toggle', roles: ['SystemAdmin'] },
  { path: '/admin/organizations', labelKey: 'shared.nav.organizations', icon: 'network', roles: ['SuperAdmin'] }
];

/** Left-hand nav shell. ALL_NAV_ITEMS is the single place to add the next destination. */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe, IconComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  readonly layout = inject(LayoutService);

  private readonly currentUrl = signal(this.router.url);
  /** Groups the user has opened by hand; navigating into a section also adds it. */
  private readonly expandedGroups = signal<ReadonlySet<string>>(new Set<string>());

  constructor() {
    this.openGroupFor(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.openGroupFor(event.urlAfterRedirects);
      });
  }

  /**
   * SuperAdmin has no operational access to any Organization's data by default
   * (docs/design/06_multi_tenant_organizations.md §1), so it gets its own filter branch: only items
   * explicitly gated to SuperAdmin show, rather than also picking up every role-unrestricted item
   * (Dashboard, Settings) the way every other role does.
   */
  readonly navItems = computed(() =>
    this.auth.hasRole('SuperAdmin')
      ? ALL_NAV_ITEMS.filter((item) => item.roles?.includes('SuperAdmin'))
      : ALL_NAV_ITEMS.filter((item) => !item.roles || item.roles.some((role) => this.auth.hasRole(role)))
  );

  isGroupExpanded(item: NavItem): boolean {
    return this.expandedGroups().has(item.path);
  }

  /** True while the router sits on the group's own page or anything below it. */
  isSectionActive(item: NavItem): boolean {
    return this.isUnder(this.currentUrl(), item.path);
  }

  toggleGroup(item: NavItem): void {
    // On the icon-only rail there is no room for child labels, so the first click widens the
    // sidebar back out and opens the group in one go rather than expanding into hidden labels.
    if (this.layout.sidebarCollapsed()) {
      this.layout.toggleSidebarCollapsed();
      this.setGroupExpanded(item.path, true);
      return;
    }
    this.setGroupExpanded(item.path, !this.isGroupExpanded(item));
  }

  private openGroupFor(url: string): void {
    for (const item of ALL_NAV_ITEMS) {
      if (item.children && this.isUnder(url, item.path)) {
        this.setGroupExpanded(item.path, true);
      }
    }
  }

  private setGroupExpanded(path: string, expanded: boolean): void {
    this.expandedGroups.update((groups) => {
      const next = new Set(groups);
      if (expanded) {
        next.add(path);
      } else {
        next.delete(path);
      }
      return next;
    });
  }

  private isUnder(url: string, path: string): boolean {
    const withoutQuery = url.split(/[?#]/)[0];
    return withoutQuery === path || withoutQuery.startsWith(`${path}/`);
  }
}
